// ============================================
// ROLC OneROLC - Home Page
// Version: 3.6.0
// ============================================

import { CONFIG } from '../config/constants.js';
import {
    getToken, getName, getRole, getIsMasterAdmin,
    setCachedWithTimestamp, getCachedWithTimestamp,
    setRole, setIsMasterAdmin, clearAuth
} from '../utils/storage.js';
import { $, $$, setContent } from '../utils/domHelpers.js';
import { capitalizeWords } from '../utils/formatters.js';
import { escapeHtml, stripHtml, showToast } from '../utils.js';
import { getSurveys } from '../api/endpoints.js';
import { logout } from '../auth/authService.js';
import { navigateToSurvey, navigateToAdmin, navigateToHome, navigateToLogin } from '../router/routes.js';

// ============================================
// Render Home Page
// ============================================
export async function renderHome(params = {}) {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const token = getToken();
    const name = getName();
    const role = getRole() || 'member';

    if (!token || !name) {
        navigateToLogin();
        return;
    }

    // Try cache first
    const cached = getCachedWithTimestamp(
        CONFIG.SURVEYS_KEY,
        CONFIG.SURVEYS_TS_KEY,
        CONFIG.CACHE_MS,
        CONFIG.SESSION_ID_KEY
    );

    if (cached) {
        renderHomeContent(cached, token, name, role);
        // Refresh in background
        refreshSurveysInBackground(token, name);
        return;
    }

    // Show skeleton while loading
    renderHomeSkeleton(name);

    try {
        const data = await getSurveys(token);
        if (data.error) {
            console.log('[DEBUG] getSurveys error:', JSON.stringify(data));
            console.log('[DEBUG] localStorage sessionId:', localStorage.getItem('rolc_session_id'));
            console.log('[DEBUG] cached_session_id:', localStorage.getItem('cached_session_id'));
            if (data.error.includes('Invalid token') || data.error.includes('Account data conflict') || data.error.includes('Session expired')) {
                showSessionConflict();
            } else {
                showError('Unable to load surveys. Please refresh.', true);
            }
            return;
        }

        const surveys = data.surveys || [];
        const userRole = data.userRole || 'member';

        // Store role from response
        if (data.userRole) setRole(data.userRole);
        if (typeof data.isMasterAdmin === 'boolean') {
            setIsMasterAdmin(data.isMasterAdmin);
        }

        // Cache surveys
        setCachedWithTimestamp(CONFIG.SURVEYS_KEY, CONFIG.SURVEYS_TS_KEY, surveys, CONFIG.SESSION_ID_KEY);

        renderHomeContent(surveys, token, name, userRole);
    } catch (err) {
        const msg = err.message || String(err);
        if (msg.includes('Invalid token') || msg.includes('Session expired') || msg.includes('Token expired')) {
            showSessionConflict();
        } else {
            showError('Unable to load surveys. Please check your connection.');
        }
    }
}

// ============================================
// Render Home Skeleton
// ============================================
function renderHomeSkeleton(name) {
    const shimmer = 'background:linear-gradient(90deg,#e5e7eb 25%,#f3f4f6 50%,#e5e7eb 75%);background-size:200% 100%;animation:shimmer 1.5s infinite;';

    setContent(`
        <style>
            @keyframes shimmer {
                0% { background-position:200% 0 }
                100% { background-position:-200% 0 }
            }
            .skel { ${shimmer} border-radius:8px }
        </style>
        <div class="card">
            <div class="welcome-box">
                <div class="welcome-label">Welcome back,</div>
                <div class="welcome-name">${escapeHtml(name.charAt(0).toUpperCase() + name.slice(1))}</div>
            </div>
            <div class="survey-card" style="background:#f9fafb;">
                <div class="survey-icon" style="opacity:0.3;">🎵</div>
                <div class="skel" style="width:60%;height:20px;margin-bottom:8px;"></div>
                <div class="skel" style="width:40%;height:14px;"></div>
            </div>
            <div class="survey-card" style="background:#f9fafb;">
                <div class="survey-icon" style="opacity:0.3;">🎵</div>
                <div class="skel" style="width:50%;height:20px;margin-bottom:8px;"></div>
                <div class="skel" style="width:35%;height:14px;"></div>
            </div>
            <div class="survey-card" style="background:#f9fafb;">
                <div class="survey-icon" style="opacity:0.3;">🎵</div>
                <div class="skel" style="width:55%;height:20px;margin-bottom:8px;"></div>
                <div class="skel" style="width:45%;height:14px;"></div>
            </div>
        </div>
        <button class="btn btn-secondary mt-2" id="btnLogoutHome">Logout</button>
    `);

    document.getElementById('btnLogoutHome')?.addEventListener('click', () => logout(true));
}

// ============================================
// Render Home Content
// ============================================
function renderHomeContent(surveys, token, name, role) {
    const isMasterAdmin = getIsMasterAdmin();
    const roleLower = String(role || '').trim().toLowerCase();
    const isAdmin = /^(admin|admins)$/i.test(roleLower) || isMasterAdmin;

    // No surveys
    if (surveys.length === 0) {
        let adminLinks = '';
        if (isAdmin) {
            adminLinks = `
                <button class="btn btn-report mt-2" id="btnControlPanel">🔧 Control Panel</button>
            `;
        }

        setContent(`
            <div class="card">
                <div class="welcome-box">
                    <div class="welcome-label">Welcome back,</div>
                    <div class="welcome-name">${escapeHtml(name.charAt(0).toUpperCase() + name.slice(1))}</div>
                </div>
                <div class="error-box" style="margin-top:20px;">
                    <div class="error-icon">📅</div>
                    <p class="error-text">No active surveys right now.<br>Please check back later.</p>
                </div>
                ${adminLinks}
                <button class="btn btn-secondary mt-2" id="btnLogoutHome">Logout</button>
            </div>
        `);

        document.getElementById('btnLogoutHome')?.addEventListener('click', () => logout(true));
        document.getElementById('btnControlPanel')?.addEventListener('click', () => navigateToAdmin());
        return;
    }

    // Build survey cards
    let cardsHtml = surveys.map(sv => `
        <div class="survey-card" data-name="${escapeHtml(sv.name)}">
            <div class="survey-icon">🎵</div>
            <div class="survey-name">${escapeHtml(sv.name)}</div>
            ${sv.description ? `<div class="survey-desc survey-description">${escapeHtml(sv.description)}</div>` : ''}
            <div class="survey-meta">${sv.eventCount || 0} question${sv.eventCount !== 1 ? 's' : ''}</div>
            <button class="btn btn-primary btn-open-survey">Open Survey</button>
        </div>
    `).join('');

    // Admin links
    let controlPanelHtml = '';

    if (isAdmin) {
        controlPanelHtml = `<button class="btn btn-report mt-2" id="btnControlPanel">🔧 Control Panel</button>`;
    }

    setContent(`
        <div class="card">
            <div class="welcome-box">
                <div class="welcome-label">Welcome back,</div>
                <div class="welcome-name">${escapeHtml(name.charAt(0).toUpperCase() + name.slice(1))}</div>
            </div>
            <div class="survey-list">
                ${cardsHtml}
            </div>
            ${controlPanelHtml}
            <button class="btn btn-secondary mt-2" id="btnLogoutHome">Logout</button>
        </div>
    `);

    // Survey card click handlers
    document.querySelectorAll('.survey-card').forEach(card => {
        card.addEventListener('click', (e) => {
            if (e.target.classList.contains('btn-open-survey')) {
                e.stopPropagation();
            }
            const surveyName = card.dataset.name;
            navigateToSurvey(surveyName);
        });
    });

    document.getElementById('btnLogoutHome')?.addEventListener('click', () => logout(true));
    document.getElementById('btnControlPanel')?.addEventListener('click', () => navigateToAdmin());
}

// ============================================
// Refresh Surveys in Background
// ============================================
async function refreshSurveysInBackground(token, name) {
    try {
        const data = await getSurveys(token);
        if (data.error) {
            console.log('[DEBUG BG] getSurveys error:', JSON.stringify(data));
            console.log('[DEBUG BG] localStorage sessionId:', localStorage.getItem('rolc_session_id'));
            // If session-related error, clear cache and show conflict dialog
            if (data.error.includes('Invalid token') || data.error.includes('Session expired') || data.error.includes('Account data conflict')) {
                clearAuth();
                if (document.getElementById('btnLogoutHome')) {
                    showSessionConflict();
                } else {
                    navigateToLogin();
                }
            }
            return;
        }
        const surveys = data.surveys || [];
        const prevRole = getRole();

        if (data.userRole) setRole(data.userRole);
        if (typeof data.isMasterAdmin === 'boolean') {
            setIsMasterAdmin(data.isMasterAdmin);
        }

        setCachedWithTimestamp(CONFIG.SURVEYS_KEY, CONFIG.SURVEYS_TS_KEY, surveys, CONFIG.SESSION_ID_KEY);

        // If role changed and home is showing, re-render
        if (prevRole !== data.userRole && document.getElementById('btnLogoutHome')) {
            renderHomeContent(surveys, token, name, data.userRole);
        }
    } catch (err) {
        console.log('Background refresh failed', err);
    }
}

// ============================================
// Error / Session helpers
// ============================================
function showError(message, isAuthError = false) {
    const buttonText = isAuthError ? 'Go to Login' : 'Retry';
    const buttonAction = isAuthError ? 'logout(false); window.location.replace("/login.html");' : 'renderHome()';

    setContent(`
        <div class="card">
            <div class="error-box">
                <div class="error-icon">⚠️</div>
                <p class="error-text">${escapeHtml(message)}</p>
                <button class="btn btn-primary" id="btnRetryHome">${buttonText}</button>
            </div>
        </div>
    `);
    document.getElementById('btnRetryHome')?.addEventListener('click', () => {
        if (isAuthError) {
            logout(false);
            window.location.replace('/login.html');
        } else {
            renderHome();
        }
    });
}

function showSessionConflict() {
    setContent(`
        <div class="card">
            <div class="error-box">
                <div class="error-icon">⚠️</div>
                <h3 class="error-text" style="font-size:1.1rem;margin-bottom:12px;">Session Conflict</h3>
                <p style="color:#6b7280;margin-bottom:16px;">You were logged in on another device or browser. This session has been replaced.</p>
                <button class="btn btn-primary" id="btnSessionReLogin">Re-Login</button>
            </div>
        </div>
    `);
    document.getElementById('btnSessionReLogin')?.addEventListener('click', () => {
        logout(false);
        navigateToLogin();
    });
}

// ============================================
// Navigation helper (removed - using import from routes.js)
// ============================================


